
/** `EntityDelta.item_kind`. Fixed by the schema, not by taste. */
export const ItemKind = {
  WEAPON: 1,
  ARMOUR: 2,
  POTION: 3,
  OTHER: 4,
  GOLD: 5,
} as const;

export type ItemKind = (typeof ItemKind)[keyof typeof ItemKind];

/** Body and edge for each shape. Warm and light against this map's dark grass. */
const PALETTE: Record<number, { readonly body: string; readonly edge: string }> = {
  [ItemKind.WEAPON]: { body: "#cfd8e3", edge: "#8b96a5" },
  [ItemKind.ARMOUR]: { body: "#b08a5a", edge: "#6f5637" },
  [ItemKind.POTION]: { body: "#e2564f", edge: "#8d2f2b" },
  [ItemKind.OTHER]: { body: "#d8c58a", edge: "#8e7c48" },
  [ItemKind.GOLD]: { body: "#ffd75a", edge: "#9b6b16" },
};

const FALLBACK = PALETTE[ItemKind.OTHER];

/** A drop reserved for somebody else. Deliberately neutral rather than merely
 * darker: a darker red potion still reads as a red potion, while the player
 * needs the immediately recognisable answer "not mine yet". */
export const CLAIMED_PALETTE = { body: "#777b82", edge: "#494c52" } as const;

/** Text colours paired with the two states. The yellow owner line follows the
 * original exactly: `SetItemTextTailOwner` renders the possessive owner at
 * `(1, 1, 0)` (`PythonTextTail.cpp:705-729`). */
export const LOOT_NAME_COLOUR = "#ffe9a8";
export const CLAIMED_NAME_COLOUR = "#9a9da3";
export const OWNER_NAME_COLOUR = "#ffff00";

export interface LootOwnership {
  readonly claimedByOther: boolean;
  readonly ownerLabel: string;
  readonly itemNameColour: string;
}

export function lootOwnership(
  owner: string,
  publicAtTick: number,
  playerName: string,
  serverTick: number,
): LootOwnership {
  const active = owner !== "" && publicAtTick > serverTick;
  const claimedByOther = active && owner !== playerName;
  return {
    claimedByOther,
    ownerLabel: active ? `${owner}'s` : "",
    itemNameColour: claimedByOther ? CLAIMED_NAME_COLOUR : LOOT_NAME_COLOUR,
  };
}

/** How far the item bobs, as a fraction of its radius. */
const BOB = 0.35;

/** One bob, in milliseconds. Slow: a fast bob reads as an error state. */
export const BOB_PERIOD_MS = 1_800;

export function bob(nowMs: number, id: number): number {
  const phase = ((nowMs / BOB_PERIOD_MS) + id * 0.37) % 1;
  return Math.sin(phase * Math.PI * 2) * BOB;
}

export function drawLoot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  kind: number,
  nowMs: number,
  id: number,
  claimed: boolean,
): void {
  const colours = claimed ? CLAIMED_PALETTE : (PALETTE[kind] ?? FALLBACK);
  const lift = bob(nowMs, id) * radius;

  ctx.save();

  // The glow, on the ground rather than on the item, so it reads as light cast
  // rather than as an outline. Dimmer for something somebody else earned, which
  // is the only place the claim window appears on screen -- and it is a hint,
  // not the rule: the rule is enforced where the item is taken.
  const glow = ctx.createRadialGradient(x, y, 0, x, y, radius * 2.6);
  glow.addColorStop(0, claimed ? "rgba(255, 236, 170, 0.10)" : "rgba(255, 236, 170, 0.34)");
  glow.addColorStop(1, "rgba(255, 236, 170, 0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(x, y, radius * 2.6, 0, Math.PI * 2);
  ctx.fill();

  // A shadow that shrinks as the item rises, which is what sells the bob as a
  // hover rather than as the whole sprite sliding up the screen.
  ctx.globalAlpha = 0.3 - lift * 0.1;
  ctx.fillStyle = "#000000";
  ctx.beginPath();
  ctx.ellipse(x, y + radius * 0.5, radius * 0.8, radius * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  ctx.translate(x, y - lift);
  ctx.fillStyle = colours.body;
  ctx.strokeStyle = colours.edge;
  ctx.lineWidth = Math.max(1, radius * 0.16);
  ctx.lineJoin = "round";

  if (kind === ItemKind.WEAPON) blade(ctx, radius);
  else if (kind === ItemKind.ARMOUR) shield(ctx, radius);
  else if (kind === ItemKind.POTION) flask(ctx, radius);
  else if (kind === ItemKind.GOLD) coins(ctx, radius);
  else bundle(ctx, radius);

  ctx.restore();
}

/** Three overlapping discs rather than the generic parcel. Yang is vnum 1 on
 *  the original server (`char_item.cpp:5253`), but it is not an inventory item;
 *  the dedicated look is what lets a person recognise what they dropped. */
function coins(ctx: CanvasRenderingContext2D, r: number): void {
  for (const [dx, dy] of [[-0.38, 0.24], [0.38, 0.24], [0, -0.28]] as const) {
    ctx.beginPath();
    ctx.ellipse(dx * r, dy * r, r * 0.5, r * 0.26, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

/** A blade on the diagonal: the one silhouette that is unmistakable at eight
 *  pixels, which is the size this is usually drawn at. */
function blade(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  ctx.moveTo(-r * 0.7, r * 0.7);
  ctx.lineTo(r * 0.75, -r * 0.75);
  ctx.lineTo(r * 0.95, -r * 0.55);
  ctx.lineTo(-r * 0.5, r * 0.9);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(-r * 0.85, r * 0.35);
  ctx.lineTo(-r * 0.25, r * 0.95);
  ctx.stroke();
}

function shield(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(r * 0.8, -r * 0.5);
  ctx.quadraticCurveTo(r * 0.8, r * 0.6, 0, r);
  ctx.quadraticCurveTo(-r * 0.8, r * 0.6, -r * 0.8, -r * 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function flask(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  ctx.arc(0, r * 0.2, r * 0.68, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.rect(-r * 0.24, -r * 0.95, r * 0.48, r * 0.55);
  ctx.fill();
  ctx.stroke();
}

function bundle(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  ctx.rect(-r * 0.75, -r * 0.6, r * 1.5, r * 1.2);
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(-r * 0.75, 0);
  ctx.lineTo(r * 0.75, 0);
  ctx.stroke();
}
